import React from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

import { ErrorBoundary } from '@/shared/features/error/components/error-boundary/error-boundary';
import { NoPermission } from '@/shared/features/error/components/no-permission/no-permission';
import { NotFound } from '@/shared/features/error/components/not-found/not-found';

const errorToString = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (isRouteErrorResponse(error)) {
    return error.statusText || error.data || 'Route Error';
  }
  return String(error);
};

const handleDeprecatedFileError = (error: string) => {
  if (
    error?.includes('Loading chunk') ||
    error?.includes('Loading CSS chunk') ||
    error?.includes('.js')
  ) {
    window.location.reload();
  }
};

// App-agnostic body for a route error: logs the error, reloads on stale-chunk
// errors, and renders the matching error screen. Each app wraps it in its own
// shell (sidebar/header) — this never pulls in app-specific route guards.
export const RouteErrorContent = () => {
  const error = useRouteError();

  React.useEffect(() => {
    const stringifiedError = errorToString(error);

    if (stringifiedError) {
      console.error('Route Error:', stringifiedError);
      handleDeprecatedFileError(stringifiedError);
    }
  }, [error]);

  if (isRouteErrorResponse(error)) {
    if (error.status === 403) {
      return <NoPermission />;
    }

    if (error.status === 404) {
      return <NotFound />;
    }
  }

  return <ErrorBoundary />;
};
